import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  MenuItem,
  Paper,
  Popper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  AutoAwesome,
  ChevronLeft,
  ChevronRight,
  Check,
  Close,
  Delete,
  OpenInFull,
  Reply,
} from "@mui/icons-material";
import axios from "axios";
import { hostname } from "../../utils";

// Model picker options are fetched from /api/v0/models and filtered by the
// "Content" tag. No frontend fallback — if the backend says a model isn't a
// content model, it doesn't appear here.
const CONTENT_MODEL_TAG = "Content";
const AI_REPLY_MAX_MODELS = 4;

interface ModelOption {
  name: string;
  label: string;
  provider: string;
}

interface ModelOutput {
  model: string;
  content: string | null;
  status: "pending" | "complete" | "error";
  error: string | null;
  finishedAt: string | null;
}

export interface ContentComment {
  id: string;
  contentId: string;
  parentCommentId: string | null;
  authorUid: string;
  authorName: string | null;
  authorEmail: string | null;
  authorAvatarUrl: string | null;
  authorType?: "user" | "ai_employee";
  authorEmployeeId?: string | null;
  aiModelOutputs?: ModelOutput[] | null;
  aiStatus?: "pending" | "complete" | "error" | null;
  body: string;
  mentionedUids: string[];
  anchorFrom: number | null;
  anchorTo: number | null;
  quotedText: string | null;
  resolved: boolean;
  resolvedByUid: string | null;
  resolvedAt: string | null;
  createdDate: string | null;
  updatedDate: string | null;
}

interface CompanyUser {
  id: string;
  uid: string | null;
  displayName: string | null;
  email: string | null;
  photoURL: string | null;
}

interface EmployeeOption {
  id: string;
  name: string | null;
  title: string | null;
  picture: string | null;
}

// Tracks a pending employee mention attached to a specific textarea until submit.
interface PendingEmployeeMention {
  employeeId: string;
  employeeName: string;
  models: string[];
}

interface Props {
  contentId: string;
  companyId: string;
  getToken: () => Promise<string>;
  comments: ContentComment[];
  setComments: React.Dispatch<React.SetStateAction<ContentComment[]>>;
  activeThreadId: string | null;
  setActiveThreadId: (id: string | null) => void;
  pendingAnchor: { from: number; to: number; quotedText: string } | null;
  clearPendingAnchor: () => void;
  currentUserUid: string | null;
  showResolved: boolean;
  setShowResolved: (v: boolean) => void;
  editorContainerRef: React.RefObject<HTMLDivElement | null>;
  editor: any;
  onCommentsChanged?: () => void;
}

const CommentsPanel: React.FC<Props> = ({
  contentId,
  companyId,
  getToken,
  comments,
  setComments,
  activeThreadId,
  setActiveThreadId,
  pendingAnchor,
  clearPendingAnchor,
  currentUserUid,
  showResolved,
  setShowResolved,
  editorContainerRef,
  editor,
  onCommentsChanged,
}) => {
  const [companyUsers, setCompanyUsers] = useState<CompanyUser[]>([]);
  const [employees, setEmployees] = useState<EmployeeOption[]>([]);
  const [modelOptions, setModelOptions] = useState<ModelOption[]>([]);
  const [newCommentBody, setNewCommentBody] = useState("");
  const [replyBody, setReplyBody] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  // One pending employee mention per textarea key ("__new" for new comment,
  // thread root id for replies). Cleared on submit.
  const [pendingEmployeeMentions, setPendingEmployeeMentions] = useState<
    Record<string, PendingEmployeeMention>
  >({});

  // When the user picks an employee from the mention dropdown, we open a
  // model multi-select dialog before completing the mention.
  const [modelPicker, setModelPicker] = useState<{
    fieldKey: string;
    employee: EmployeeOption;
    onConfirm: (models: string[]) => void;
    onCancel: () => void;
  } | null>(null);

  // Thread popped out for comfortable reading (handles very long AI answers).
  const [expandedThreadId, setExpandedThreadId] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const token = await getToken();
        const [usersRes, employeesRes] = await Promise.all([
          axios.get(`${hostname}/company_users/${companyId}?page=1&per_page=100`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${hostname}/companies/${companyId}/employees`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);
        const users = usersRes.data?.company_users || [];
        setCompanyUsers(Array.isArray(users) ? users : []);
        const emps = employeesRes.data?.employees || [];
        setEmployees(
          (Array.isArray(emps) ? emps : []).map((e: any) => ({
            id: e.id,
            name: e.name,
            title: e.title,
            picture: e.picture,
          }))
        );
      } catch (err) {
        console.error("Failed to load mention targets:", err);
      }
    })();
  }, [companyId, getToken]);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${hostname}/api/v0/models`, {
          params: { companyId },
        });
        const all = res.data?.models || [];
        const contentModels: ModelOption[] = all
          .filter(
            (m: any) =>
              Array.isArray(m.tags) && m.tags.includes(CONTENT_MODEL_TAG)
          )
          .map((m: any) => ({
            name: m.name,
            label: m.label || m.name,
            provider: m.provider || "",
          }));
        setModelOptions(contentModels);
      } catch (err) {
        console.error("Failed to load content models:", err);
      }
    })();
  }, [companyId]);

  const threads = useMemo(() => {
    const roots = comments.filter((c) => !c.parentCommentId);
    return roots
      .filter((r) => (showResolved ? true : !r.resolved))
      .map((root) => ({
        root,
        replies: comments
          .filter((c) => c.parentCommentId === root.id)
          .sort((a, b) => (a.createdDate || "").localeCompare(b.createdDate || "")),
      }));
  }, [comments, showResolved]);

  // Only user @mentions (uids) go into mentionedUids. Employee mentions are
  // tracked separately in pendingEmployeeMentions (they don't need notifications).
  const extractUserMentions = (body: string): string[] => {
    const uids: string[] = [];
    const re = /@\[([^\]]+)\]\(([^)]+)\)/g;
    let m;
    while ((m = re.exec(body)) !== null) {
      if (!m[2].startsWith("employee:")) {
        uids.push(m[2]);
      }
    }
    return uids;
  };

  const triggerAiReply = async (
    parentCommentId: string,
    employeeId: string,
    models: string[]
  ) => {
    try {
      const token = await getToken();
      await axios.post(
        `${hostname}/content/${contentId}/comments/${parentCommentId}/ai-reply`,
        { companyId, employeeId, models },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onCommentsChanged?.();
    } catch (err) {
      console.error("Failed to trigger AI reply:", err);
    }
  };

  const submitNewComment = async () => {
    if (!pendingAnchor || !newCommentBody.trim()) return;
    setSubmitting(true);
    try {
      const token = await getToken();
      const res = await axios.post(
        `${hostname}/content/${contentId}/comments`,
        {
          companyId,
          body: newCommentBody,
          mentionedUids: extractUserMentions(newCommentBody),
          anchorFrom: pendingAnchor.from,
          anchorTo: pendingAnchor.to,
          quotedText: pendingAnchor.quotedText,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const createdId = res.data.comment.id;
      const pendingEmp = pendingEmployeeMentions["__new"];
      setNewCommentBody("");
      clearPendingAnchor();
      setActiveThreadId(createdId);
      setPendingEmployeeMentions((prev) => {
        const next = { ...prev };
        delete next["__new"];
        return next;
      });
      onCommentsChanged?.();
      if (pendingEmp && pendingEmp.models.length > 0) {
        triggerAiReply(createdId, pendingEmp.employeeId, pendingEmp.models);
      }
    } catch (err) {
      console.error("Failed to create comment:", err);
    } finally {
      setSubmitting(false);
    }
  };

  const submitReply = async (threadRootId: string) => {
    const body = (replyBody[threadRootId] || "").trim();
    if (!body) return;
    setSubmitting(true);
    try {
      const token = await getToken();
      const res = await axios.post(
        `${hostname}/content/${contentId}/comments`,
        {
          companyId,
          body,
          parentCommentId: threadRootId,
          mentionedUids: extractUserMentions(body),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const createdId = res.data.comment.id;
      const pendingEmp = pendingEmployeeMentions[threadRootId];
      setReplyBody((prev) => ({ ...prev, [threadRootId]: "" }));
      setPendingEmployeeMentions((prev) => {
        const next = { ...prev };
        delete next[threadRootId];
        return next;
      });
      onCommentsChanged?.();
      if (pendingEmp && pendingEmp.models.length > 0) {
        triggerAiReply(createdId, pendingEmp.employeeId, pendingEmp.models);
      }
    } catch (err) {
      console.error("Failed to reply:", err);
    } finally {
      setSubmitting(false);
    }
  };

  const resolveThread = async (threadRootId: string, resolved: boolean) => {
    try {
      const token = await getToken();
      const res = await axios.post(
        `${hostname}/content/${contentId}/comments/${threadRootId}/resolve`,
        { companyId, resolved },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onCommentsChanged?.();
    } catch (err) {
      console.error("Failed to resolve:", err);
    }
  };

  const deleteComment = async (commentId: string) => {
    try {
      const token = await getToken();
      await axios.delete(
        `${hostname}/content/${contentId}/comments/${commentId}?companyId=${companyId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onCommentsChanged?.();
    } catch (err) {
      console.error("Failed to delete:", err);
    }
  };

  // Get the Y offset of the START of a comment's highlight span relative to the
  // editor container's scroll content. Uses getClientRects() to get the first
  // line rect for multi-line inline spans (getBoundingClientRect gives the full box).
  const ANCHOR_NUDGE = 36; // px — nudge up so card content aligns with text start

  const getAnchorTop = useCallback(
    (commentId: string): number | null => {
      const container = editorContainerRef?.current;
      if (!container) return null;
      const span = container.querySelector(
        `[data-comment-id="${commentId}"]`
      ) as HTMLElement | null;
      if (!span) return null;
      const containerRect = container.getBoundingClientRect();
      const rects = span.getClientRects();
      const firstRect = rects.length > 0 ? rects[0] : span.getBoundingClientRect();
      return Math.max(0, firstRect.top - containerRect.top + container.scrollTop - ANCHOR_NUDGE);
    },
    [editorContainerRef]
  );

  const getPendingAnchorTop = useCallback((): number | null => {
    if (!pendingAnchor || !editor?.view) return null;
    const container = editorContainerRef?.current;
    if (!container) return null;
    try {
      const coords = editor.view.coordsAtPos(pendingAnchor.from);
      const containerRect = container.getBoundingClientRect();
      return Math.max(0, coords.top - containerRect.top + container.scrollTop - ANCHOR_NUDGE);
    } catch {
      return null;
    }
  }, [pendingAnchor, editor, editorContainerRef]);

  // Sync the panel's scroll position with the editor's scroll
  const panelScrollRef = useRef<HTMLDivElement | null>(null);
  const [layoutTick, setLayoutTick] = useState(0);
  useEffect(() => {
    const editorEl = editorContainerRef?.current;
    if (!editorEl) return;
    const handler = () => {
      setLayoutTick((n) => n + 1);
      // Keep the panel scrolled in sync with the editor
      if (panelScrollRef.current) {
        panelScrollRef.current.scrollTop = editorEl.scrollTop;
      }
    };
    editorEl.addEventListener("scroll", handler, { passive: true });
    window.addEventListener("resize", handler);
    return () => {
      editorEl.removeEventListener("scroll", handler);
      window.removeEventListener("resize", handler);
    };
  }, [editorContainerRef]);

  // Measure rendered card heights via refs so we can compute accurate margins
  const cardRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // Build a unified sorted list of items: existing threads + pending new comment form.
  // Each item has a type, anchorY, and a key for rendering.
  type LayoutItem =
    | { kind: "thread"; root: ContentComment; replies: ContentComment[]; anchorY: number }
    | { kind: "pending"; anchorY: number };

  const layoutItems = useMemo((): LayoutItem[] => {
    const items: LayoutItem[] = threads.map((t) => ({
      kind: "thread" as const,
      ...t,
      anchorY: getAnchorTop(t.root.id) ?? 0,
    }));
    if (pendingAnchor) {
      items.push({
        kind: "pending" as const,
        anchorY: getPendingAnchorTop() ?? 0,
      });
    }
    items.sort((a, b) => a.anchorY - b.anchorY);
    return items;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threads, getAnchorTop, getPendingAnchorTop, pendingAnchor, layoutTick, comments]);

  // Compute marginTop for each item so it aligns with its anchor Y.
  const cardMargins = useMemo(() => {
    const margins: number[] = [];
    let currentBottom = 0;
    const GAP = 8;
    for (let i = 0; i < layoutItems.length; i++) {
      const targetY = layoutItems[i].anchorY;
      const margin = Math.max(GAP, targetY - currentBottom);
      margins.push(margin);
      const key = layoutItems[i].kind === "thread" ? (layoutItems[i] as any).root.id : "__pending__";
      const el = cardRefs.current[key];
      const cardHeight = el ? el.offsetHeight : 160;
      currentBottom = currentBottom + margin + cardHeight;
    }
    return margins;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layoutItems, layoutTick]);

  // After cards render and refs are populated, re-layout with actual measured heights
  useEffect(() => {
    const timer = requestAnimationFrame(() => setLayoutTick((n) => n + 1));
    return () => cancelAnimationFrame(timer);
  }, [comments, showResolved]);

  return (
    <Box
      sx={{
        width: 360,
        minWidth: 360,
        borderLeft: 1,
        borderColor: "divider",
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.paper",
        alignSelf: "stretch",
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: 1,
          borderColor: "divider",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="subtitle1" fontWeight={600}>
          Comments
        </Typography>
        <Button
          size="small"
          onClick={() => setShowResolved(!showResolved)}
          sx={{ textTransform: "none" }}
        >
          {showResolved ? "Hide resolved" : "Show resolved"}
        </Button>
      </Box>

      <Box
        ref={panelScrollRef}
        sx={{
          flex: 1,
          overflow: "auto",
          px: 1,
          pt: 0,
          pb: 2,
          minHeight: 0,
        }}
      >
        {layoutItems.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", mt: 4 }}>
            Select text in the editor to add a comment.
          </Typography>
        )}

        {layoutItems.map((item, idx) => {
          if (item.kind === "pending") {
            return (
              <Paper
                key="__pending__"
                ref={(el: HTMLDivElement | null) => { cardRefs.current["__pending__"] = el; }}
                variant="outlined"
                sx={{
                  p: 1.5,
                  mt: `${cardMargins[idx] ?? 8}px`,
                  bgcolor: "action.hover",
                  borderColor: "primary.main",
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mb: 0.5 }}
                >
                  Commenting on:
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontStyle: "italic",
                    mb: 1,
                    maxHeight: 60,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  "{pendingAnchor?.quotedText}"
                </Typography>
                <MentionTextField
                  value={newCommentBody}
                  onChange={setNewCommentBody}
                  users={companyUsers}
                  employees={employees}
                  placeholder="Add a comment… (use @ to mention a person or AI employee)"
                  autoFocus
                  onRequestEmployeeModels={(employee, onConfirm, onCancel) =>
                    setModelPicker({
                      fieldKey: "__new",
                      employee,
                      onConfirm: (models) => {
                        setPendingEmployeeMentions((prev) => ({
                          ...prev,
                          __new: {
                            employeeId: employee.id,
                            employeeName: employee.name || "AI Employee",
                            models,
                          },
                        }));
                        onConfirm(models);
                        setModelPicker(null);
                      },
                      onCancel: () => {
                        onCancel();
                        setModelPicker(null);
                      },
                    })
                  }
                />
                <Box sx={{ display: "flex", gap: 1, mt: 1, justifyContent: "flex-end" }}>
                  <Button size="small" onClick={clearPendingAnchor}>
                    Cancel
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={submitNewComment}
                    disabled={submitting || !newCommentBody.trim()}
                  >
                    Comment
                  </Button>
                </Box>
              </Paper>
            );
          }
          const { root, replies } = item;
          return (
          <Paper
            key={root.id}
            ref={(el: HTMLDivElement | null) => { cardRefs.current[root.id] = el; }}
            variant="outlined"
            sx={{
              p: 1.5,
              mt: `${cardMargins[idx] ?? 8}px`,
              borderColor: activeThreadId === root.id ? "primary.main" : "divider",
              bgcolor: root.resolved ? "action.hover" : "background.paper",
              opacity: root.resolved ? 0.7 : 1,
              cursor: "pointer",
            }}
            onClick={() => setActiveThreadId(root.id)}
          >
            <Box sx={{ display: "flex", justifyContent: "flex-end", mb: -0.5 }}>
              <Tooltip title="Expand thread">
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setExpandedThreadId(root.id);
                  }}
                  sx={{ p: 0.25 }}
                >
                  <OpenInFull sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            </Box>
            <Box sx={{ maxHeight: 380, overflow: "auto", pr: 0.5 }}>
              {root.quotedText && (
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    fontStyle: "italic",
                    color: "text.secondary",
                    borderLeft: 3,
                    borderColor: "primary.main",
                    pl: 1,
                    mb: 1,
                    maxHeight: 40,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  "{root.quotedText}"
                </Typography>
              )}
              <CommentItem
                comment={root}
                currentUserUid={currentUserUid}
                onDelete={() => deleteComment(root.id)}
              />
              {replies.map((r) => (
                <Box key={r.id} sx={{ ml: 2, mt: 1, pt: 1, borderTop: 1, borderColor: "divider" }}>
                  {r.authorType === "ai_employee" ? (
                    <AiCommentItem comment={r} modelOptions={modelOptions} />
                  ) : (
                    <CommentItem
                      comment={r}
                      currentUserUid={currentUserUid}
                      onDelete={() => deleteComment(r.id)}
                    />
                  )}
                </Box>
              ))}
            </Box>

            {!root.resolved && (
              <Box sx={{ mt: 1.5 }} onClick={(e) => e.stopPropagation()}>
                <MentionTextField
                  value={replyBody[root.id] || ""}
                  onChange={(v) => setReplyBody((prev) => ({ ...prev, [root.id]: v }))}
                  users={companyUsers}
                  employees={employees}
                  placeholder="Reply… (use @ to mention a person or AI employee)"
                  onRequestEmployeeModels={(employee, onConfirm, onCancel) =>
                    setModelPicker({
                      fieldKey: root.id,
                      employee,
                      onConfirm: (models) => {
                        setPendingEmployeeMentions((prev) => ({
                          ...prev,
                          [root.id]: {
                            employeeId: employee.id,
                            employeeName: employee.name || "AI Employee",
                            models,
                          },
                        }));
                        onConfirm(models);
                        setModelPicker(null);
                      },
                      onCancel: () => {
                        onCancel();
                        setModelPicker(null);
                      },
                    })
                  }
                />
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mt: 1,
                  }}
                >
                  <Tooltip title="Resolve thread">
                    <IconButton size="small" onClick={() => resolveThread(root.id, true)}>
                      <Check fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<Reply />}
                    onClick={() => submitReply(root.id)}
                    disabled={submitting || !(replyBody[root.id] || "").trim()}
                  >
                    Reply
                  </Button>
                </Box>
              </Box>
            )}

            {root.resolved && (
              <Box sx={{ mt: 1, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Chip label="Resolved" size="small" color="primary" variant="outlined" />
                <Button size="small" onClick={(e) => { e.stopPropagation(); resolveThread(root.id, false); }}>
                  Reopen
                </Button>
              </Box>
            )}
          </Paper>
          );
        })}
      </Box>

      {modelPicker && (
        <ModelPickerDialog
          employee={modelPicker.employee}
          modelOptions={modelOptions}
          initialSelection={
            pendingEmployeeMentions[modelPicker.fieldKey]?.models || []
          }
          onConfirm={modelPicker.onConfirm}
          onCancel={modelPicker.onCancel}
        />
      )}
      {expandedThreadId && (() => {
        const root = comments.find(
          (c) => c.id === expandedThreadId && !c.parentCommentId
        );
        if (!root) return null;
        const threadReplies = comments
          .filter((c) => c.parentCommentId === root.id)
          .sort((a, b) => (a.createdDate || "").localeCompare(b.createdDate || ""));
        return (
          <ThreadDialog
            root={root}
            replies={threadReplies}
            currentUserUid={currentUserUid}
            modelOptions={modelOptions}
            onClose={() => setExpandedThreadId(null)}
            onDelete={(commentId) => deleteComment(commentId)}
          />
        );
      })()}
    </Box>
  );
};

const ModelPickerDialog: React.FC<{
  employee: EmployeeOption;
  modelOptions: ModelOption[];
  initialSelection: string[];
  onConfirm: (models: string[]) => void;
  onCancel: () => void;
}> = ({ employee, modelOptions, initialSelection, onConfirm, onCancel }) => {
  const [selected, setSelected] = useState<string[]>(() =>
    initialSelection.filter((m) => modelOptions.some((o) => o.name === m))
  );

  const toggle = (name: string) => {
    setSelected((prev) => {
      if (prev.includes(name)) {
        return prev.filter((m) => m !== name);
      }
      if (prev.length >= AI_REPLY_MAX_MODELS) return prev;
      return [...prev, name];
    });
  };

  return (
    <Dialog open onClose={onCancel} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Avatar
            src={employee.picture || undefined}
            sx={{ width: 32, height: 32 }}
          >
            {(employee.name || "?").charAt(0).toUpperCase()}
          </Avatar>
          <Box>
            <Typography variant="subtitle1" fontWeight={600}>
              Ask {employee.name || "AI Employee"}
            </Typography>
            {employee.title && (
              <Typography variant="caption" color="text.secondary">
                {employee.title}
              </Typography>
            )}
          </Box>
        </Box>
        <IconButton
          onClick={onCancel}
          sx={{ position: "absolute", right: 8, top: 8 }}
          size="small"
        >
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Pick one or more models. The employee will answer with each, and
          you can swipe through the responses.
        </Typography>
        {modelOptions.length === 0 ? (
          <Typography variant="body2" color="text.secondary" fontStyle="italic">
            Loading available models…
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {modelOptions.map((m) => {
              const checked = selected.includes(m.name);
              const atMax = !checked && selected.length >= AI_REPLY_MAX_MODELS;
              return (
                <FormControlLabel
                  key={m.name}
                  control={
                    <Checkbox
                      checked={checked}
                      disabled={atMax}
                      onChange={() => toggle(m.name)}
                    />
                  }
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography variant="body2">{m.label}</Typography>
                      {m.provider && (
                        <Chip label={m.provider} size="small" sx={{ height: 20 }} />
                      )}
                    </Box>
                  }
                />
              );
            })}
          </Box>
        )}
        <Typography variant="caption" color="text.secondary">
          {selected.length} of {AI_REPLY_MAX_MODELS} selected
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          variant="contained"
          disabled={selected.length === 0}
          onClick={() => onConfirm(selected)}
        >
          Add to comment
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ThreadDialog: React.FC<{
  root: ContentComment;
  replies: ContentComment[];
  currentUserUid: string | null;
  modelOptions: ModelOption[];
  onClose: () => void;
  onDelete: (commentId: string) => void;
}> = ({ root, replies, currentUserUid, modelOptions, onClose, onDelete }) => {
  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        Comment thread
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
          size="small"
        >
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {root.quotedText && (
          <Typography
            variant="body2"
            sx={{
              display: "block",
              fontStyle: "italic",
              color: "text.secondary",
              borderLeft: 3,
              borderColor: "primary.main",
              pl: 1.5,
              mb: 2,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            "{root.quotedText}"
          </Typography>
        )}
        <CommentItem
          comment={root}
          currentUserUid={currentUserUid}
          onDelete={() => onDelete(root.id)}
        />
        {replies.map((r) => (
          <Box
            key={r.id}
            sx={{ ml: 3, mt: 2, pt: 2, borderTop: 1, borderColor: "divider" }}
          >
            {r.authorType === "ai_employee" ? (
              <AiCommentItem comment={r} modelOptions={modelOptions} />
            ) : (
              <CommentItem
                comment={r}
                currentUserUid={currentUserUid}
                onDelete={() => onDelete(r.id)}
              />
            )}
          </Box>
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

const AiCommentItem: React.FC<{
  comment: ContentComment;
  modelOptions: ModelOption[];
}> = ({ comment, modelOptions }) => {
  const outputs = (comment.aiModelOutputs || []) as ModelOutput[];
  const [index, setIndex] = useState(0);
  const total = outputs.length;
  const safeIndex = Math.min(index, Math.max(0, total - 1));
  const current = total > 0 ? outputs[safeIndex] : null;

  const go = (delta: number) => {
    if (total === 0) return;
    setIndex((i) => (i + delta + total) % total);
  };

  const modelLabel = (name: string): string => {
    const o = modelOptions.find((x) => x.name === name);
    return o ? o.label : name;
  };

  return (
    <Box sx={{ display: "flex", gap: 1 }}>
      <Avatar
        src={comment.authorAvatarUrl || undefined}
        sx={{ width: 28, height: 28, fontSize: 12 }}
      >
        {(comment.authorName || "?").charAt(0).toUpperCase()}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            mb: 0.5,
          }}
        >
          <Typography variant="caption" fontWeight={600}>
            {comment.authorName || "AI Employee"}
          </Typography>
        </Box>

        {total > 1 && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              mb: 0.5,
            }}
          >
            <IconButton size="small" onClick={() => go(-1)}>
              <ChevronLeft fontSize="small" />
            </IconButton>
            <Typography variant="caption" color="text.secondary" sx={{ flex: 1, textAlign: "center" }}>
              {current ? modelLabel(current.model) : ""} · {safeIndex + 1}/{total}
            </Typography>
            <IconButton size="small" onClick={() => go(1)}>
              <ChevronRight fontSize="small" />
            </IconButton>
          </Box>
        )}
        {total === 1 && current && (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
            {modelLabel(current.model)}
          </Typography>
        )}

        {!current && (
          <Typography variant="body2" color="text.secondary" fontStyle="italic">
            Thinking…
          </Typography>
        )}
        {current && current.status === "pending" && (
          <Typography variant="body2" color="text.secondary" fontStyle="italic">
            {modelLabel(current.model)} is thinking…
          </Typography>
        )}
        {current && current.status === "error" && (
          <Typography variant="body2" color="error">
            {current.error || "Model failed to respond."}
          </Typography>
        )}
        {current && current.status === "complete" && (
          <Typography
            variant="body2"
            sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
          >
            {current.content || ""}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const CommentItem: React.FC<{
  comment: ContentComment;
  currentUserUid: string | null;
  onDelete: () => void;
}> = ({ comment, currentUserUid, onDelete }) => {
  const rendered = useMemo(() => renderCommentBody(comment.body), [comment.body]);
  return (
    <Box sx={{ display: "flex", gap: 1 }}>
      <Avatar
        src={comment.authorAvatarUrl || undefined}
        sx={{ width: 28, height: 28, fontSize: 12 }}
      >
        {(comment.authorName || "?").charAt(0).toUpperCase()}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="caption" fontWeight={600}>
            {comment.authorName || comment.authorEmail || "Unknown"}
          </Typography>
          {comment.authorUid === currentUserUid && (
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); onDelete(); }}>
              <Delete sx={{ fontSize: 14 }} />
            </IconButton>
          )}
        </Box>
        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
          {rendered}
        </Typography>
      </Box>
    </Box>
  );
};

// Renders body with @[Name](uid) tokens as bold mention spans
const renderCommentBody = (body: string): React.ReactNode => {
  const parts: React.ReactNode[] = [];
  const re = /@\[([^\]]+)\]\(([^)]+)\)/g;
  let lastIndex = 0;
  let m;
  let key = 0;
  while ((m = re.exec(body)) !== null) {
    if (m.index > lastIndex) {
      parts.push(body.slice(lastIndex, m.index));
    }
    parts.push(
      <Box
        key={key++}
        component="span"
        sx={{ color: "primary.main", fontWeight: 600 }}
      >
        @{m[1]}
      </Box>
    );
    lastIndex = m.index + m[0].length;
  }
  if (lastIndex < body.length) {
    parts.push(body.slice(lastIndex));
  }
  return parts.length > 0 ? parts : body;
};

// @mention input: typing "@" opens a dropdown of company users AND AI
// employees. Mentions render as inline pill chips inside a contentEditable
// div; the wire format on onChange/value is still "@[Name](uid)" — for
// employee mentions the uid is "employee:<id>".
type MentionCandidate =
  | { kind: "user"; user: CompanyUser }
  | { kind: "employee"; employee: EmployeeOption };

const MENTION_CHIP_CLASS = "mention-chip";
const MENTION_TOKEN_RE = /@\[([^\]]+)\]\(([^)]+)\)/g;

// Walk the contentEditable DOM and emit the plain-text wire format. Chip
// elements emit "@[Name](uid)"; text/whitespace/<br> emit their plain form.
const serializeDom = (root: HTMLElement): string => {
  let out = "";
  const walk = (n: Node) => {
    if (n.nodeType === Node.TEXT_NODE) {
      out += n.textContent || "";
      return;
    }
    if (n.nodeType !== Node.ELEMENT_NODE) return;
    const el = n as HTMLElement;
    const mentionUid = el.dataset?.mentionUid;
    if (mentionUid !== undefined) {
      const name = el.dataset.mentionName || el.textContent?.replace(/^@/, "") || "";
      out += `@[${name}](${mentionUid})`;
      return;
    }
    if (el.tagName === "BR") {
      out += "\n";
      return;
    }
    if (el.tagName === "DIV" && out.length > 0 && !out.endsWith("\n")) {
      out += "\n";
    }
    el.childNodes.forEach((child) => walk(child));
  };
  root.childNodes.forEach((child) => walk(child));
  return out;
};

// Build a chip element for a mention. `contentEditable=false` makes it
// behave as an atomic unit (single Backspace deletes the whole chip).
const makeChip = (name: string, uid: string): HTMLSpanElement => {
  const span = document.createElement("span");
  span.className = MENTION_CHIP_CLASS;
  span.dataset.mentionUid = uid;
  span.dataset.mentionName = name;
  span.dataset.mentionKind = uid.startsWith("employee:") ? "employee" : "user";
  span.contentEditable = "false";
  span.textContent = `@${name}`;
  return span;
};

// Hydrate the editor div from a wire-format string (used on external value
// changes, e.g. form cleared after submit).
const hydrateDom = (root: HTMLElement, value: string) => {
  root.innerHTML = "";
  let lastIndex = 0;
  MENTION_TOKEN_RE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = MENTION_TOKEN_RE.exec(value)) !== null) {
    if (m.index > lastIndex) {
      root.appendChild(
        document.createTextNode(value.slice(lastIndex, m.index))
      );
    }
    root.appendChild(makeChip(m[1], m[2]));
    lastIndex = m.index + m[0].length;
  }
  if (lastIndex < value.length) {
    root.appendChild(document.createTextNode(value.slice(lastIndex)));
  }
};

const MentionTextField: React.FC<{
  value: string;
  onChange: (v: string) => void;
  users: CompanyUser[];
  employees: EmployeeOption[];
  placeholder?: string;
  autoFocus?: boolean;
  onRequestEmployeeModels?: (
    employee: EmployeeOption,
    onConfirm: (models: string[]) => void,
    onCancel: () => void
  ) => void;
}> = ({
  value,
  onChange,
  users,
  employees,
  placeholder,
  autoFocus,
  onRequestEmployeeModels,
}) => {
  const editorRef = useRef<HTMLDivElement | null>(null);
  const lastPushedValue = useRef(value);
  const [showDropdown, setShowDropdown] = useState(false);
  const [query, setQuery] = useState("");
  const [highlightIdx, setHighlightIdx] = useState(0);
  const [focused, setFocused] = useState(false);
  // Tracks the text range (inside the current text node) that represents the
  // active "@query" typed by the user; used to replace it with a chip on pick.
  const pendingMentionRange = useRef<{
    node: Text;
    start: number;
    end: number;
  } | null>(null);

  // Hydrate editor when the external value diverges from what we last pushed.
  // IMPORTANT: because the editor is contentEditable, calling hydrateDom wipes
  // the DOM that holds the caret and snaps the caret to the start. We therefore
  // only re-hydrate when the external value is genuinely different from what
  // the editor's own DOM currently serializes to — and we always compare
  // against the live serialization so round-trip whitespace differences
  // (e.g. NBSP) never trigger a spurious wipe.
  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    const current = serializeDom(el);
    if (value === current) {
      lastPushedValue.current = value;
      return;
    }
    if (value === lastPushedValue.current) {
      // External consumer hasn't asked for a different value — don't touch.
      return;
    }
    hydrateDom(el, value);
    lastPushedValue.current = value;
  }, [value]);

  // Initial hydrate on mount.
  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    if (el.childNodes.length === 0 && value) {
      hydrateDom(el, value);
      lastPushedValue.current = value;
    }
    if (autoFocus) {
      el.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const emitChange = useCallback(() => {
    const el = editorRef.current;
    if (!el) return;
    const v = serializeDom(el);
    lastPushedValue.current = v;
    onChange(v);
  }, [onChange]);

  // Inspect the current selection: if the caret sits inside a text node and
  // there's a "@<word>" immediately before it (with whitespace or start-of-input
  // before the @), open the dropdown and remember the range to replace.
  const syncMentionState = useCallback(() => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || !sel.isCollapsed) {
      setShowDropdown(false);
      pendingMentionRange.current = null;
      return;
    }
    const el = editorRef.current;
    if (!el) return;
    const range = sel.getRangeAt(0);
    const node = range.startContainer;
    if (!el.contains(node) || node.nodeType !== Node.TEXT_NODE) {
      setShowDropdown(false);
      pendingMentionRange.current = null;
      return;
    }
    const text = node.textContent || "";
    const caret = range.startOffset;
    let i = caret - 1;
    while (i >= 0 && !/\s/.test(text[i]) && text[i] !== "@") i--;
    if (i < 0 || text[i] !== "@") {
      setShowDropdown(false);
      pendingMentionRange.current = null;
      return;
    }
    // The character immediately before "@" must be whitespace or the very
    // start of the text node (which should itself either be at the start
    // of the editor or follow a chip/line break).
    const prevChar = i > 0 ? text[i - 1] : null;
    if (prevChar !== null && !/\s/.test(prevChar)) {
      setShowDropdown(false);
      pendingMentionRange.current = null;
      return;
    }
    pendingMentionRange.current = {
      node: node as Text,
      start: i,
      end: caret,
    };
    const nextQuery = text.slice(i + 1, caret);
    setShowDropdown(true);
    setQuery((prev) => {
      if (prev !== nextQuery) {
        // Query changed (user typed/deleted a char) — reset highlight to top.
        setHighlightIdx(0);
      }
      return nextQuery;
    });
  }, []);

  const handleInput = useCallback(() => {
    emitChange();
    syncMentionState();
  }, [emitChange, syncMentionState]);

  const filtered = useMemo<MentionCandidate[]>(() => {
    const q = query.toLowerCase();
    const userMatches: MentionCandidate[] = users
      .filter((u) => {
        if (!u.uid) return false;
        const name = (u.displayName || "").toLowerCase();
        const email = (u.email || "").toLowerCase();
        return name.includes(q) || email.includes(q);
      })
      .map((u) => ({ kind: "user" as const, user: u }));
    const employeeMatches: MentionCandidate[] = employees
      .filter((e) => {
        const name = (e.name || "").toLowerCase();
        const title = (e.title || "").toLowerCase();
        return name.includes(q) || title.includes(q);
      })
      .map((e) => ({ kind: "employee" as const, employee: e }));

    const MAX_TOTAL = 20;
    if (q.length > 0) {
      return [...employeeMatches, ...userMatches].slice(0, MAX_TOTAL);
    }
    const HALF = Math.floor(MAX_TOTAL / 2);
    const employeesHead = employeeMatches.slice(0, HALF);
    const usersHead = userMatches.slice(0, MAX_TOTAL - employeesHead.length);
    const employeesExtra = employeeMatches.slice(
      employeesHead.length,
      employeesHead.length + (MAX_TOTAL - employeesHead.length - usersHead.length)
    );
    return [...employeesHead, ...usersHead, ...employeesExtra].slice(0, MAX_TOTAL);
  }, [users, employees, query]);

  const insertChip = useCallback(
    (name: string, uid: string): boolean => {
      const range = pendingMentionRange.current;
      const el = editorRef.current;
      if (!range || !el) return false;
      const { node, start, end } = range;
      // Split the text node around the "@query" span and replace that span
      // with a chip + trailing space.
      const before = (node.textContent || "").slice(0, start);
      const after = (node.textContent || "").slice(end);
      const parent = node.parentNode;
      if (!parent) return false;

      const chip = makeChip(name, uid);
      const spaceNode = document.createTextNode("\u00A0");
      const afterNode = document.createTextNode(after);

      node.textContent = before;
      parent.insertBefore(chip, node.nextSibling);
      parent.insertBefore(spaceNode, chip.nextSibling);
      parent.insertBefore(afterNode, spaceNode.nextSibling);

      // Move caret to right after the inserted space.
      const sel = window.getSelection();
      if (sel) {
        const r = document.createRange();
        r.setStart(spaceNode, spaceNode.length);
        r.collapse(true);
        sel.removeAllRanges();
        sel.addRange(r);
      }

      pendingMentionRange.current = null;
      setShowDropdown(false);
      setQuery("");
      emitChange();
      return true;
    },
    [emitChange]
  );

  const removeChipByUid = useCallback(
    (uid: string) => {
      const el = editorRef.current;
      if (!el) return;
      const chips = el.querySelectorAll<HTMLElement>(
        `.${MENTION_CHIP_CLASS}[data-mention-uid="${CSS.escape(uid)}"]`
      );
      chips.forEach((c) => {
        // Also remove the trailing non-breaking space we added on insert.
        const next = c.nextSibling;
        if (
          next &&
          next.nodeType === Node.TEXT_NODE &&
          (next.textContent === "\u00A0" || next.textContent === " ")
        ) {
          next.parentNode?.removeChild(next);
        }
        c.parentNode?.removeChild(c);
      });
      emitChange();
    },
    [emitChange]
  );

  const pickCandidate = useCallback(
    (cand: MentionCandidate) => {
      if (cand.kind === "user") {
        const name = cand.user.displayName || cand.user.email || "user";
        insertChip(name, String(cand.user.uid));
        return;
      }
      const emp = cand.employee;
      const name = emp.name || "AI Employee";
      const uid = `employee:${emp.id}`;
      if (!insertChip(name, uid)) return;
      if (onRequestEmployeeModels) {
        onRequestEmployeeModels(
          emp,
          () => {
            /* confirmed — parent stored the pending mention */
          },
          () => {
            // cancelled — strip the chip back out
            removeChipByUid(uid);
          }
        );
      }
    },
    [insertChip, removeChipByUid, onRequestEmployeeModels]
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (showDropdown && filtered.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlightIdx((prev) => Math.min(prev + 1, filtered.length - 1));
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlightIdx((prev) => Math.max(prev - 1, 0));
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        pickCandidate(filtered[highlightIdx]);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setShowDropdown(false);
        return;
      }
    }
    if (e.key === "Enter") {
      // Use a line-break element instead of a <div> so serialization stays
      // clean across browsers.
      e.preventDefault();
      document.execCommand("insertLineBreak");
      emitChange();
    }
  };

  const dropdownOpen = showDropdown && filtered.length > 0;
  const isEmpty = (value || "").length === 0;

  return (
    <Box
      sx={{
        position: "relative",
        "& .mention-chip": {
          display: "inline-flex",
          alignItems: "center",
          px: 0.75,
          py: 0,
          mx: 0.25,
          borderRadius: 999,
          bgcolor: "primary.main",
          color: "primary.contrastText",
          fontSize: "0.8rem",
          lineHeight: 1.6,
          whiteSpace: "nowrap",
          userSelect: "all",
          cursor: "default",
        },
        "& .mention-chip[data-mention-kind='employee']": {
          bgcolor: "secondary.main",
          color: "secondary.contrastText",
        },
      }}
    >
      <Box
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        onKeyUp={syncMentionState}
        onClick={syncMentionState}
        onFocus={() => setFocused(true)}
        onBlur={() =>
          // Delay so a dropdown click can fire first
          setTimeout(() => setFocused(false), 150)
        }
        sx={{
          minHeight: 56,
          width: "100%",
          fontFamily: "inherit",
          fontSize: "0.875rem",
          lineHeight: 1.5,
          padding: focused ? "7.5px 13px" : "8.5px 14px",
          borderRadius: 1,
          border: focused ? "2px solid" : "1px solid",
          borderColor: focused ? "primary.main" : "rgba(0,0,0,0.23)",
          outline: "none",
          boxSizing: "border-box",
          background: "transparent",
          color: "inherit",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          overflowWrap: "anywhere",
        }}
      />
      {isEmpty && placeholder && (
        <Box
          sx={{
            position: "absolute",
            left: 14,
            top: 8,
            color: "text.disabled",
            pointerEvents: "none",
            fontSize: "0.875rem",
            lineHeight: 1.5,
          }}
        >
          {placeholder}
        </Box>
      )}
      {dropdownOpen && (
        <Paper
          elevation={8}
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            top: "100%",
            mt: 0.5,
            maxHeight: 320,
            overflow: "auto",
            zIndex: 1600,
          }}
        >
          <List dense disablePadding>
            {filtered.map((cand, idx) => {
              const isEmployee = cand.kind === "employee";
              const key = isEmployee ? `emp:${cand.employee.id}` : `usr:${cand.user.id}`;
              const name = isEmployee
                ? cand.employee.name || "AI Employee"
                : cand.user.displayName || cand.user.email || "User";
              const sub = isEmployee
                ? cand.employee.title || "AI Employee"
                : cand.user.displayName
                ? cand.user.email
                : null;
              const avatarSrc = isEmployee
                ? cand.employee.picture || undefined
                : cand.user.photoURL || undefined;
              return (
                <MenuItem
                  key={key}
                  selected={idx === highlightIdx}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    pickCandidate(cand);
                  }}
                  onMouseEnter={() => setHighlightIdx(idx)}
                >
                  <ListItemAvatar sx={{ minWidth: 36 }}>
                    <Avatar src={avatarSrc} sx={{ width: 24, height: 24, fontSize: 11 }}>
                      {name.charAt(0).toUpperCase()}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={name}
                    secondary={sub}
                    primaryTypographyProps={{ variant: "body2" }}
                    secondaryTypographyProps={{ variant: "caption" }}
                  />
                </MenuItem>
              );
            })}
          </List>
        </Paper>
      )}
    </Box>
  );
};

export default CommentsPanel;
